import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@brent/core-ui/ui'
import { Plus } from 'lucide-react'
import { useSurveys, useCreateSurvey } from '@/hooks/useSurveys'
import { useToast } from '@/hooks/use-toast'
import SurveyForm from '@/components/features/surveys/SurveyForm'
import type { CreateSurveyInput, Survey } from '@/services/api/surveys'

function getEffectiveStatus(survey: Survey): string {
  if (survey.status === 'draft') return 'draft'
  if (!survey.opens_at || !survey.closes_at) return 'draft'
  const now = new Date()
  const opens = new Date(survey.opens_at)
  const closes = new Date(survey.closes_at)
  if (now < opens) return 'scheduled'
  if (now >= closes) return 'closed'
  return 'open'
}

const statusVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  scheduled: 'outline',
  open: 'default',
  closed: 'outline',
}

export function Surveys() {
  const navigate = useNavigate()
  const { data: surveys, isLoading, error } = useSurveys()
  const createMutation = useCreateSurvey()
  const { toast } = useToast()
  const [createOpen, setCreateOpen] = useState(false)

  const handleCreate = async (input: CreateSurveyInput) => {
    try {
      const survey = await createMutation.mutateAsync(input)
      toast({ title: 'Survey created', description: 'The survey has been saved as a draft.' })
      setCreateOpen(false)
      navigate(`/surveys/${survey.survey_id}`)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to create survey',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Surveys</h1>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Surveys</h1>
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load surveys. {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Surveys</h1>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Survey
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Survey</DialogTitle>
              <DialogDescription>
                Create a survey with Likert-scale questions and a free-text question. It will start
                as a draft.
              </DialogDescription>
            </DialogHeader>
            <SurveyForm onSubmit={handleCreate} isPending={createMutation.isPending} />
          </DialogContent>
        </Dialog>
      </div>

      {!surveys || surveys.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No surveys yet</CardTitle>
            <CardDescription>Create your first survey to gather member feedback.</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-24">Questions</TableHead>
                  <TableHead className="w-36">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {surveys.map((survey) => {
                  const effectiveStatus = getEffectiveStatus(survey)
                  return (
                    <TableRow
                      key={survey.survey_id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/surveys/${survey.survey_id}`)}
                    >
                      <TableCell className="font-medium">
                        {survey.title.length > 80
                          ? survey.title.slice(0, 80) + '...'
                          : survey.title}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariants[effectiveStatus] ?? 'outline'}>
                          {effectiveStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>{survey.likert_questions.length + 1}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(survey.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
